import { User, AdminUser } from '../types';
import { userRepository } from '../../data/userRepository';
import { orderRepository } from '../../data/orderRepository';
import { auth, db } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';
import { TIME } from '../constants';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup, 
  sendEmailVerification, 
  updateProfile, 
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

let creationPromise: Promise<User | null> | null = null;

export const authService = {
    
    async checkSession(): Promise<User | null> {
        logger.info("[Auth] Verificando sesión...");
        try {
            await setPersistence(auth, browserLocalPersistence);
        } catch (e) {
            logger.error('[AuthService] Error de persistencia', e);
        }

        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
                if (firebaseUser) {
                    logger.info("[Auth] Sesión activa encontrada", { email: firebaseUser.email });
                    unsubscribe();
                    const user = await this.handleFirebaseUser(firebaseUser);
                    resolve(user);
                }
            });

            // Si en 2.5s no hay respuesta, asumimos que no hay sesión
      setTimeout(() => {
        unsubscribe();
        if (!auth.currentUser) {
          logger.info("[Auth] Sin sesión previa");
          resolve(null);
        }
      }, 2500);
        });
    },

async handleFirebaseUser(firebaseUser: FirebaseUser, extraData?: { phone?: string, name?: string, dni?: string }): Promise<User | null> {
        if (!firebaseUser.uid) return null;
        if (creationPromise) return creationPromise;

        creationPromise = (async () => {
            let dbUser: User | null = null;
            
            // Try to get user from Firestore
            try {
                const foundUser = await userRepository.getUserById(firebaseUser.uid);
                dbUser = foundUser || null;
                
                // --- Account Migration (Link old Email-based docs to UID) ---
                if (!dbUser && firebaseUser.email) {
                    logger.info("[Auth] UID no encontrado, verificando existencia por email...");
                    const oldUser = await userRepository.getUserByEmail(firebaseUser.email);
                    if (oldUser) {
                        logger.info("[Auth] Migrando cuenta antigua a UID...");
                        dbUser = { ...oldUser, id: firebaseUser.uid };
                        await setDoc(doc(db, 'users', firebaseUser.uid), dbUser);
                    }
                }
            } catch (error) {
                // If error reading user (likely Firestore rules issue), assume new user
                logger.info("[Auth] Usuario nuevo o error leyendo documento, se creará automáticamente");
            }

            if (dbUser) return dbUser as User;

            // Create regular user
            const newUser: AdminUser = {
                id: firebaseUser.uid,
                name: extraData?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuario',
                email: firebaseUser.email || '',
                emailVerified: firebaseUser.emailVerified,
                phone: extraData?.phone || '',
                dni: extraData?.dni || '',
                avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email?.charAt(0) || 'U'}&background=random`,
                points: 0,
                walletBalance: 0,
                level: 1,
                role: 'customer',
                isAdmin: false,
                completedTasks: [],
                activeRewards: [],
                participatingRaffles: [],
                wonAuctions: [],
                addresses: [],
                joinDate: new Date().toISOString(),
                totalSpent: 0,
                ordersCount: 0,
                status: 'active'
            };

            await setDoc(doc(db, 'users', newUser.id), newUser);
            logger.info(`[Auth] New user document created: ${newUser.email}`);
            return newUser as User;
        })();

        return creationPromise;
    },

    async login(email?: string, password?: string): Promise<{ success: boolean; user?: User; error?: string }> {
        if (!email || !password) return { success: false, error: "Email y contraseña requeridos" };
        try {
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = await this.handleFirebaseUser(userCredential.user);
            return user ? { success: true, user } : { success: false, error: "Error al cargar perfil" };
        } catch {
            return { success: false, error: "Credenciales incorrectas" };
        }
    },

    async loginWithGoogle(): Promise<{ success: boolean; user?: User; error?: string }> {
        try {
            logger.info("[Auth] Iniciando Login con Google (Popup Mode)...");
            await setPersistence(auth, browserLocalPersistence);
            const provider = new GoogleAuthProvider();
            
            // Usamos Popup para todos. En mobile Android es hoy más estable que Redirect 
            // cuando se navega en dominios personalizados con HashRouter.
            const result = await signInWithPopup(auth, provider);
            
            if (result.user) {
                const user = await this.handleFirebaseUser(result.user);
                
                // Link guest orders after Google login
                if (user && result.user.email) {
                    const linkedOrders = await orderRepository.linkGuestOrdersToUser(
                        result.user.email, 
                        user.dni || '', 
                        user.id
                    );
                    if (linkedOrders.length > 0) {
                        logger.info(`[Auth] Linked ${linkedOrders.length} guest orders after Google login`);
                    }
                }
                
                return user ? { success: true, user } : { success: false, error: "Error al sincronizar perfil" };
            }
            return { success: false, error: "No se obtuvieron datos de Google" };
        } catch (error) {
            const err = error as { code?: string; message?: string };
            logger.error('[AuthService] Google Login Error', error);
            if (err.code === 'auth/popup-blocked') {
                return { success: false, error: "El navegador bloqueó la ventana. Por favor habilita los popups." };
            }
      return { success: false, error: "Error al iniciar sesión con Google" };
    }
  },

  async loginWithFacebook(): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      logger.info("[Auth] Iniciando Login con Facebook...");
      await setPersistence(auth, browserLocalPersistence);
      const provider = new FacebookAuthProvider();
      
      // Request additional permissions
      provider.addScope('email');
      provider.addScope('public_profile');
      
      const result = await signInWithPopup(auth, provider);
      
      if (result.user) {
        const user = await this.handleFirebaseUser(result.user);
        
        // Link guest orders after Facebook login
        if (user && result.user.email) {
          const linkedOrders = await orderRepository.linkGuestOrdersToUser(
            result.user.email,
            user.dni || '',
            user.id
          );
          if (linkedOrders.length > 0) {
            logger.info(`[Auth] Linked ${linkedOrders.length} guest orders after Facebook login`);
          }
        }
        
        return user ? { success: true, user } : { success: false, error: "Error al sincronizar perfil" };
      }
      return { success: false, error: "No se obtuvieron datos de Facebook" };
    } catch (error) {
      const err = error as { code?: string; message?: string };
      logger.error('[AuthService] Facebook Login Error', error);
      if (err.code === 'auth/popup-blocked') {
        return { success: false, error: "El navegador bloqueó la ventana. Por favor habilita los popups." };
      }
      if (err.code === 'auth/account-exists-with-different-credential') {
        return { success: false, error: "Ya existe una cuenta con este email usando otro método de inicio de sesión." };
      }
      return { success: false, error: "Error al iniciar sesión con Facebook" };
    }
  },

  async loginWithApple(): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      logger.info("[Auth] Iniciando Login con Apple...");
      await setPersistence(auth, browserLocalPersistence);
      const provider = new OAuthProvider('apple.com');
      
      // Request scopes
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      
      if (result.user) {
        const user = await this.handleFirebaseUser(result.user);
        
        // Link guest orders after Apple login
        if (user && result.user.email) {
          const linkedOrders = await orderRepository.linkGuestOrdersToUser(
            result.user.email,
            user.dni || '',
            user.id
          );
          if (linkedOrders.length > 0) {
            logger.info(`[Auth] Linked ${linkedOrders.length} guest orders after Apple login`);
          }
        }
        
        return user ? { success: true, user } : { success: false, error: "Error al sincronizar perfil" };
      }
      return { success: false, error: "No se obtuvieron datos de Apple" };
    } catch (error) {
      const err = error as { code?: string; message?: string };
      logger.error('[AuthService] Apple Login Error', error);
      if (err.code === 'auth/popup-blocked') {
        return { success: false, error: "El navegador bloqueó la ventana. Por favor habilita los popups." };
      }
      return { success: false, error: "Error al iniciar sesión con Apple" };
    }
  },

  async logout(): Promise<void> {
        await signOut(auth);
    },

    async register(data: { name: string; email: string; password: string; phone: string; dni: string }): Promise<{ success: boolean; user?: User; linkedOrders?: string[]; error?: string }> {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
            await updateProfile(userCredential.user, { displayName: data.name });
            const user = await this.handleFirebaseUser(userCredential.user, { phone: data.phone, name: data.name, dni: data.dni });
            
            if (user) {
                // Link existing guest orders to new account
                const linkedOrders = await orderRepository.linkGuestOrdersToUser(data.email, data.dni, user.id);
                if (linkedOrders.length > 0) {
                    logger.info(`[Auth] Linked ${linkedOrders.length} guest orders to new user`, { 
                        userId: user.id, 
                        email: data.email 
                    });
                }
                return { success: true, user, linkedOrders };
            }
            
            return { success: false, error: "Error al crear perfil" };
        } catch (error) {
            const err = error as { message?: string };
            logger.error('[AuthService] Registration error', error);
            return { success: false, error: err.message || "Error desconocido" };
        }
    },

    async sendVerificationEmail(): Promise<boolean> {
        if (auth.currentUser) {
            try { 
                await sendEmailVerification(auth.currentUser); 
                return true; 
            } catch (error) {
                logger.error('[AuthService] Failed to send verification email', error);
                return false; 
            }
        }
        return false;
    }
};